import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  notificationsApi,
  subscribeNotificationCreatedSignals,
  useAppDispatch,
  useGetNotificationPreferencesQuery,
  useListNotificationsQuery,
  type Notification as OntologyNotification
} from '@axilon-apps/domains-ontology-ui-state'
import { useBrowserNotificationsPreference } from '../../lib/browser-notifications-preference'
import { showOntologyBrowserNotification, useBrowserNotificationPermission } from './browser-notify'
import { presentNotification } from './notification-presentation'
import { configureNotificationServiceWorker } from './notification-sw-client'

/** id → last occurrence stamp; a folded repeat keeps its id but moves the stamp. */
type SeenStamps = Map<string, string>

function occurrenceStamp(n: OntologyNotification): string {
  return n.last_occurred_at ?? n.created_at
}

/** Unread Needs-you rows that are new, or happened again, since `seen`. */
export function findFreshNeedsYou(
  list: readonly OntologyNotification[],
  seen: SeenStamps
): OntologyNotification[] {
  return list.filter(
    (n) =>
      n.read !== true &&
      presentNotification(n).bucket === 'action' &&
      seen.get(n.id) !== occurrenceStamp(n)
  )
}

/** Match the bell / popover poll so we share one RTK cache entry. */
const POLLING_INTERVAL_MS = 30_000
/** Keep SW auth headers fresh (token rotation / branch changes). */
const SW_CONFIG_REFRESH_MS = 60_000

/**
 * Global watcher: when a Needs-you row arrives (or happens again) after the
 * initial seed, show an in-app toast while the tab is visible, or a desktop
 * notification while it is hidden, each per the person's settings.
 *
 * Background delivery uses a Service Worker that polls independently — Chrome
 * freezes background tabs so page SSE/timers alone are not enough. SSE still
 * invalidates the RTK list while the page is runnable; SW showNotification is
 * the OS toast path.
 */
export function BrowserNotificationsWatcher() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const localPreference = useBrowserNotificationsPreference()
  const { data: prefs } = useGetNotificationPreferencesQuery()
  const permission = useBrowserNotificationPermission()
  const desktopWanted = localPreference && prefs?.desktop_notifications !== false
  const toastsWanted = prefs?.in_app_toasts !== false
  const desktopArmed = desktopWanted && permission === 'granted'
  const { data } = useListNotificationsQuery(
    { state: 'open' },
    { pollingInterval: POLLING_INTERVAL_MS }
  )
  const seenIdsRef = React.useRef<SeenStamps | null>(null)
  const navigateRef = React.useRef(navigate)
  navigateRef.current = navigate

  // Register / configure the SW poller. Refresh headers on an interval so a
  // rotated JWT still works while the tab sits in the background.
  React.useEffect(() => {
    void configureNotificationServiceWorker(desktopArmed)
    if (!desktopArmed) return
    const timer = window.setInterval(() => {
      void configureNotificationServiceWorker(true)
    }, SW_CONFIG_REFRESH_MS)
    return () => {
      window.clearInterval(timer)
      void configureNotificationServiceWorker(false)
    }
  }, [desktopArmed])

  // Keep SSE open while alerts can fire (do not tear down on document.hidden).
  React.useEffect(() => {
    if (!desktopArmed && !toastsWanted) return

    const controller = new AbortController()
    void subscribeNotificationCreatedSignals({
      onSignal: () => {
        dispatch(
          notificationsApi.util.invalidateTags([
            { type: 'Notification', id: 'LIST' },
            'NotificationCounts'
          ])
        )
      },
      signal: controller.signal
    })
    return () => controller.abort()
  }, [desktopArmed, toastsWanted, dispatch])

  React.useEffect(() => {
    if (data === undefined) return
    const list: OntologyNotification[] = data
    const current: SeenStamps = new Map(list.map((n) => [n.id, occurrenceStamp(n)]))
    if (seenIdsRef.current === null) {
      seenIdsRef.current = current
      return
    }
    const fresh = findFreshNeedsYou(list, seenIdsRef.current)
    seenIdsRef.current = current
    const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'
    for (const n of fresh) {
      const href = presentNotification(n).href ?? '/notifications'
      if (visible && toastsWanted) {
        toast(n.title, {
          id: `notification-${n.id}`,
          description: n.context ?? undefined,
          action: { label: 'Open', onClick: () => navigateRef.current(href) }
        })
      } else if (!visible && desktopArmed) {
        void showOntologyBrowserNotification(n, () => {
          window.focus()
          navigateRef.current(href)
        })
      }
    }
  }, [data, desktopArmed, toastsWanted])

  return null
}
